import React, { useState, useEffect, useRef, useMemo } from 'react';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import { useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import { getDiningTableService, deleteDiningTableService } from '@/services/DiningTableServices';
import { cn } from '@/lib/utils';
import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import AddEditDrawer from './component/AddEditDrawer';
import PritntPdfTable from '@/components/common/PritntPdfTable';
import PermissionCheck from '@/components/common/PermissionCheck';

const DiningTablesPage = ({ pageTitle }) => {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('DiningTable Data');

  // Show Branch column when viewing ALL branches
  const rawBranchPref = (typeof localStorage !== 'undefined' && localStorage.getItem('branch_id')) || '';
  const showBranchCol = !rawBranchPref || rawBranchPref.toLowerCase() === 'all' || rawBranchPref === '*';

  const [dataSource, setDataSource] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [editDataId, setEditDataId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const filterDivRef = useRef(null);
  const [dataFilter, setDataFilter] = useState({ search: '', page: 1, limit: 10, extra_filter: {} });

  const tableColumn = useMemo(() => {
    const cols = [
      { name: 'Name',  isIncludePrint: true },
      { name: 'Size',  isIncludePrint: true },
    ];
    if (showBranchCol) cols.push({ name: 'Branch', isIncludePrint: true });
    cols.push({ name: 'Status', isIncludePrint: true }, { name: 'Action', isIncludePrint: false });
    return cols;
  }, [showBranchCol]);

  useEffect(() => { getTableData(); }, [dataFilter]); // eslint-disable-line

  const handleDrawerOpen = (editId) => {
    // Prevent creating/editing in ALL branches (server also enforces)
    if (!rawBranchPref || rawBranchPref.toLowerCase() === 'all' || rawBranchPref === '*') {
      Swal.fire('Select a branch first', 'You cannot create or edit dining tables in "All" scope.', 'warning');
      return;
    }
    setEditDataId(editId || '');
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  };

  const onChangeFilter = (name, value) => setDataFilter((p) => ({ ...p, [name]: value }));

  const handleFilterToggle = () => {
    if (!filterDivRef.current) return;
    setIsActive((p) => !p);
    filterDivRef.current.style.height = !isActive ? '100%' : '0px';
  };

  const getTableData = () => {
    setIsLoading(true);
    dispatch(getDiningTableService(dataFilter))
      .then((res) => {
        setTotalRecord(res?.data?.data?.total || 0);
        setDataSource(res?.data?.data?.data || []);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const deleteRecord = (deleteId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (!result.isConfirmed) return;
      setIsLoading(true);
      dispatch(deleteDiningTableService(deleteId))
        .then(() => { Swal.fire('Deleted!', 'Your record has been deleted.', 'success'); getTableData(); })
        .catch(({ message, errorData }) => validationErrors(errorData, message))
        .finally(() => setIsLoading(false));
    });
  };

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />
      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection onChangeParPage={onChangeFilter} limit={dataFilter.limit} />
            </div>

            <button className="db-card-filter-btn table-filter-btn" onClick={handleFilterToggle}>
              <i className="lab lab-filter lab-font-size-14" />
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[{ icon: 'lab-printer-line', text: 'Print', dropDownAction: handleDownloadPdf }]}
            />

            <PermissionCheck permsission="create_dining_tables">
              <button type="button" onClick={() => handleDrawerOpen('')} className="db-btn h-[37px] text-white bg-primary">
                <i className="lab lab-add-circle-line" />
                Add Dining Table
              </button>
            </PermissionCheck>
          </div>
        </CardHeader>

        <div className={cn('table-filter-div', isActive ? 'active' : '')} ref={filterDivRef}>
          <DataTableFilter onChangeFilter={onChangeFilter} />
        </div>

        <div ref={printTableRef}>
          <DataTable
            isLoading={isLoading}
            tableColumn={tableColumn}
            dataSource={dataSource}
            totalRecord={totalRecord}
            dataFilter={dataFilter}
            onChangePage={onChangeFilter}
            handleDrawerOpen={handleDrawerOpen}
            deleteRecord={deleteRecord}
            showBranchCol={showBranchCol}
          />
        </div>

        <AddEditDrawer
          titleText={pageTitle}
          getTableData={getTableData}
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          editDataId={editDataId}
        />
      </Card>
    </>
  );
};

export default DiningTablesPage;
